import React, { useState, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { get, range, startCase } from 'lodash';
import { useLazyQuery } from '@apollo/client';
import { Radio } from 'antd';
import ActionButton from 'common/components/action-button';
import { dayFormatType, formatDay } from '~helpers/day';
import { sortDirectionMapping } from '~constants/index';
import { QUERY_LISTORDERS } from '../queries';
import { handleExportToExcel } from '../utils/export-excel-manual';
import generatePath from '~settings/routing';
import {
  bookingJourneyType,
  leadGenerationStageText,
  fullAutomaticStageText,
} from 'common/constants/booking';
import { formatTenancyLength } from 'common/helpers/formatTenancyLength';
import { convertEmptyArrayToNull } from 'common/helpers/convertEmptyArrayToNull';
import { searchFilterAddon, generateExcelData } from './helpers';
import { getTriggerPointOptions } from 'common/helpers/getTriggerPointOptions';
import { canUpdateBookingStatus } from 'common/helpers/canUpdateBookingStatus';

export const useBookingList = ({ bookingJourneys }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [tableData, setTableData] = useState();
  const [filters, setFilters] = useState({});
  const [exportLoading, setExportLoading] = useState(false);
  const [exportVisible, setExportVisible] = useState(false);
  const [checkedIndex, setCheckedIndex] = useState(0);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [stage, setStage] = useState(null);

  const [queryBookingData, { loading }] = useLazyQuery(QUERY_LISTORDERS, {
    fetchPolicy: 'no-cache',
    variables: { bookingJourneys },
    onCompleted: data => {
      if (!exportLoading) {
        setTableData(data.listOrders);
      } else if (exportLoading && data.listOrders?.edges?.length > 0) {
        const excelData = generateExcelData(data.listOrders?.edges, checkedIndex, statusFilter);
        handleExportToExcel(excelData, () => {
          setExportLoading(false);
          setExportVisible(false);
        });
      } else {
        setExportLoading(false);
        setExportVisible(false);
      }
    },
  });

  const handleTableChange = (page, filter, sorter) => {
    const pageSize = page.pageSize !== filters?.pageSize ? page?.pageSize : filters?.pageSize;
    const pageNumber = filters?.pageSize && page.pageSize !== filters?.pageSize ? 1 : page.current;

    let params = {
      bookingJourneys,
      referenceId: convertEmptyArrayToNull(filter.referenceId),
      studentName: convertEmptyArrayToNull(filter.studentName),
      passportFullName: convertEmptyArrayToNull(filter.passportFullName),
      stages: convertEmptyArrayToNull(filter.status),
      propertyName: convertEmptyArrayToNull(filter.propertyName),
      commissionTriggerPoint: convertEmptyArrayToNull(filter.commissionTriggerPoint, true),
    };
    const variables = {
      ...params,
      pageNumber: pageNumber ?? 1,
      pageSize: pageSize ?? 10,
    };

    if (sorter?.columnKey && sorter?.order) {
      variables.sortBy = sorter?.columnKey === 'created_date' ? 'CREATED_AT' : sorter?.columnKey;
      variables.sortDirection = sortDirectionMapping[sorter?.order];
    }

    setFilters(variables);
    queryBookingData({ variables });
  };

  const handleChecked = idx => setCheckedIndex(idx);

  const handleDownloadBooking = () => {
    setExportLoading(true);
    queryBookingData({
      variables: {
        ...filters,
        pageSize: 1000,
        pageNumber: checkedIndex + 1,
      },
    });
  };

  const handleOpenStatusModal = (e, id, stage) => {
    e.stopPropagation();
    e.preventDefault();
    setStatusModalVisible(true);
    setBookingId(id);
    setStage(stage);
  };

  const handleStatusUpdated = () => {
    queryBookingData({ variables: filters });
  };

  const handleOnRowClick = record => {
    const url = generatePath('booking.bookingDetail', {
      id: record.node?.referenceId,
      bookingJourney: bookingJourneys[0],
    });
    history.push(url);
  };

  const exportData = useMemo(() => {
    let modalData = [];
    if (tableData?.totalCount % 1000 === 0) {
      modalData = range(tableData?.totalCount / 1000 - 1);
    } else {
      modalData = range(tableData?.totalCount / 1000);
    }
    return modalData.map(item => ({ value: item }));
  }, [tableData?.totalCount]);

  const statusFilter = useMemo(() => {
    const bookingJourney = bookingJourneys[0];
    let StatusObj = leadGenerationStageText;
    if (bookingJourney === bookingJourneyType.FULL_AUTOMATIC) {
      StatusObj = fullAutomaticStageText;
    }
    return Object.keys(StatusObj).map(status => ({
      text: get(StatusObj, status) || '-',
      value: status,
    }));
  }, [bookingJourneys]);

  const orderColumns = useMemo(() => {
    const columns = [
      {
        title: t('cms.booking.property.table.booking_number'),
        key: 'referenceId',
        fixed: 'left',
        width: 180,
        render: (_, record) => (
          <span className="transfer__table-field">{record?.node?.referenceId || '-'}</span>
        ),
        ...searchFilterAddon('referenceId'),
      },
      {
        title: t('cms.booking.property.table.student_name'),
        key: 'studentName',
        width: 160,
        render: (_, record) => (
          <div className="transfer__table-field">{record?.node?.student?.fullName || '-'}</div>
        ),
        ...searchFilterAddon('studentName'),
      },
      {
        title: t('cms.booking.property.table.snapshot_student_name'),
        key: 'passportFullName',
        width: 220,
        render: (_, record) => (
          <div className="transfer__table-field">{record?.node?.passportFullName || '-'}</div>
        ),
        ...searchFilterAddon('passportFullName'),
      },
      {
        title: t('cms.booking.property.table.landlord_name'),
        key: 'name',
        width: 160,
        render: (text, record) => <span>{record?.node?.landlord?.name || '-'}</span>,
      },
      {
        title: t('cms.booking.property.table.property_name'),
        key: 'propertyName',
        width: 200,
        render: (_, record) => (
          <span className="transfer__table-field">{record?.node?.property?.name || '-'}</span>
        ),
        ...searchFilterAddon('propertyName'),
      },
      {
        title: t('cms.booking.property.table.move_in_date'),
        key: 'booked_move_in_date',
        width: 180,
        render: (_, record) => (
          <span className="transfer__table-field">
            {record?.node?.moveInDate
              ? formatDay(dayFormatType.CONTENTDATE, record?.node?.moveInDate)
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.booking.property.table.move_out_date'),
        key: 'booked_move_out_date',
        width: 180,
        render: (_, record) => (
          <span className="transfer__table-field">
            {record?.node?.moveOutDate
              ? formatDay(dayFormatType.CONTENTDATE, record?.node?.moveOutDate)
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.booking.property.table.tenancy_length'),
        key: 'booked_tenancy_length',
        width: 200,
        render: (_, record) => (
          <span className="transfer__table-field">
            {formatTenancyLength(
              record?.node?.tenancyLength?.weeks,
              record?.node?.tenancyLength?.days,
            )}
          </span>
        ),
      },
    ];

    if (bookingJourneyType.LEAD_GENERATION === bookingJourneys[0]) {
      columns.push({
        title: t('cms.booking.property.table.commission_trigger_point'),
        key: 'commissionTriggerPoint',
        width: 250,
        filters: getTriggerPointOptions(t),
        filterMultiple: false,
        render: (_, record) => (
          <span className="transfer__table-field">
            {record?.node?.commissionTriggerPoint
              ? startCase(
                  getTriggerPointOptions(t).find(
                    item => item.value === record?.node?.commissionTriggerPoint,
                  )?.text,
                )
              : '-'}
          </span>
        ),
      });
    }

    columns.push(
      {
        title: t('cms.booking.property.table.commission'),
        key: 'total_commission_charged',
        width: 180,
        render: (_, record) => (
          <span className="transfer__table-field">
            {record?.node?.commissions?.length > 0 &&
            record?.node?.commissions[0]?.currency &&
            record?.node?.commissions[0]?.totalPrice !== undefined
              ? record?.node?.commissions[0]?.currency + record?.node?.commissions[0]?.totalPrice
              : '-'}
          </span>
        ),
      },

      {
        title: t('cms.booking.property.table.booking_status'),
        key: 'status',
        width: 180,
        filters: statusFilter,
        render: (_, record) => (
          <span className="transfer__table-field">
            {record?.node?.stage
              ? statusFilter.find(t => t.value === record?.node?.stage)?.text
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.booking.property.table.created_date'),
        key: 'created_date',
        width: 184,
        sorter: tableData?.edges?.length > 0,
        render: (_, record) => (
          <span className="transfer__table-field">
            {record?.node?.createdAt
              ? formatDay(dayFormatType.UKWITHHM, record?.node?.createdAt)
              : '-'}
          </span>
        ),
      },
    );

    if (bookingJourneyType.LEAD_GENERATION === bookingJourneys[0]) {
      columns.push({
        title: t('cms.booking.property.table.action'),
        className: 'booking-table__header-action',
        key: 'can_update_list_order_stage',
        width: 150,
        fixed: 'right',
        render: (_, record) => (
          <ActionButton
            disabled={
              !canUpdateBookingStatus(record?.node?.stage, record?.node?.canUpdateListOrderStage)
            }
            onClick={e => handleOpenStatusModal(e, record?.node?.id, record?.node?.stage)}
          >
            {t('cms.property.commission.action.update_status')}
          </ActionButton>
        ),
      });
    }

    return columns;
  }, [bookingJourneys, tableData]);

  const exportColumns = useMemo(
    () => [
      {
        title: '',
        dataIndex: 'value',
        key: 'value',
        render: (_, record) => {
          const value = record.value;
          return (
            <Radio
              checked={checkedIndex === value}
              className="radio-item"
              onClick={() => handleChecked(value)}
            >
              <span style={{ marginRight: 12 }}>{t('cms.sidebar.menu.booking')}</span>
              <span className="num-wrapper">{value * 1000 + 1}</span>
              <span className="dash">-</span>
              <span className="num-wrapper">
                {value === exportData.length - 1 ? tableData?.totalCount : (value + 1) * 1000}
              </span>
            </Radio>
          );
        },
      },
    ],
    [checkedIndex, exportData],
  );

  useEffect(() => {
    queryBookingData();
  }, []);

  return {
    exportData,
    statusFilter,
    loading,
    orderColumns,
    exportColumns,
    statusModalVisible,
    exportVisible,
    checkedIndex,
    exportLoading,
    tableData,
    filters,
    bookingId,
    stage,
    t,
    setStatusModalVisible,
    setExportVisible,
    setCheckedIndex,
    setBookingId,
    setStage,
    handleTableChange,
    handleChecked,
    handleDownloadBooking,
    handleOpenStatusModal,
    handleOnRowClick,
    handleStatusUpdated,
  };
};
